import { clip, type Clip } from '../clip.ts';
import { TAU, inCubic, inOut, inQuad, keys, linear, outBack, outCubic, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';

/*
 * The players' allies: the druid's bear, the worker golems, the artificer's clockwork (automaton, colossus,
 * spider-bot) and the saga's caravan. Conventions as in human.ts: a limb hanging along -Y swings forward with a
 * negative X; +X on a body leans it forward; the figure faces +Z. `L` bones are on -X, `R` bones on +X.
 */

const clampP = (x: number): number => (x < 0 ? 0 : x);

/* ---------- the druid's bear: a heavy moss-and-stone animal, organic overlap everywhere ---------- */

type BB = 'root' | 'figure' | 'body' | 'head' | 'jaw' | 'frontL' | 'elbowL' | 'wristL' | 'frontR' | 'elbowR' | 'wristR' | 'hindL' | 'hockL' | 'footL' | 'hindR' | 'hockR' | 'footR';
type BP = KeyPose<BB>;

/** One leg over its cycle `u` (0 = touchdown): the stance pushes it back, the swing folds it and reaches. */
const quadLeg = (u: number, A: number, duty: number): { swing: number; fold: number; reach: number; load: number } => {
  if (u < duty) {
    const k = u / duty;
    return { swing: -A + 2 * A * k, fold: 0, reach: 0, load: Math.sin(Math.PI * k) };
  }
  const k = (u - duty) / (1 - duty);
  return { swing: A - 2 * A * inOut(k), fold: Math.sin(Math.PI * Math.min(1, k * 1.15)) ** 0.9, reach: span(k, 0.55, 0.9) * (1 - span(k, 0.9, 1)), load: 0 };
};

const bearIdle = (): Clip<BB> =>
  clip<BB>('idle', 4.4, true, (b, t) => {
    const u = phase(t, 4.4);
    const p = u * TAU;
    // Two slow breaths a loop; the head hangs low and swings, and every so often it lifts to sniff the air.
    const br = Math.sin(p * 2);
    b.body.rot(-0.015 * br, 0.02 * Math.sin(p), 0.015 * Math.sin(p)).scale(1 + 0.012 * br);
    b.root.pos(0, -0.008 * (1 + br), 0).rot(0, 0, 0.012 * Math.sin(p));
    const sniffUp = span(u, 0.42, 0.52) * (1 - span(u, 0.72, 0.84));
    const sniff = sniffUp * Math.max(0, Math.sin(p * 14)) ** 3;
    b.head.rot(0.12 - 0.35 * sniffUp - 0.04 * sniff + 0.03 * br, 0.18 * Math.sin(p + 0.8) * (1 - sniffUp), 0.05 * Math.sin(p + 0.3));
    b.jaw.rot(0.06 * sniff + 0.03 * Math.max(0, br));
    // Weight shifts from paw to paw; the right forepaw kneads the ground once.
    b.frontL.rot(0.02 * Math.sin(p), 0, 0);
    b.frontR.rot(-0.02 * Math.sin(p), 0, 0);
    const knead = span(u, 0.12, 0.2) * (1 - span(u, 0.24, 0.34));
    b.elbowR.rot(0.35 * knead);
    b.wristR.rot(0.5 * knead);
    b.frontR.rot(-0.12 * knead).pos(0, 0.04 * knead, 0);
    b.hindL.rot(0, 0, -0.015 * Math.sin(p));
    b.hindR.rot(0, 0, -0.015 * Math.sin(p));
  }, (t) => {
    const br = Math.sin(phase(t, 4.4) * TAU * 2);
    return [1 + 0.012 * br, 1 + 0.006 * br, 1];
  });

/**
 * The bear's walk: diagonal pairs (left hind with right fore, a beat apart), the shoulder of the planted leg
 * riding up, the body rolling over it, the head low and swinging against the stride.
 */
const bearWalk = (dur = 1.2): Clip<BB> =>
  clip<BB>('walk', dur, true, (b, t) => {
    const u = phase(t, dur);
    const A = 0.3, D = 0.62;
    const hL = quadLeg(u, A, D);
    const fR = quadLeg((u + 1 - 0.07) % 1, A * 0.95, D);
    const hR = quadLeg((u + 0.5) % 1, A, D);
    const fL = quadLeg((u + 0.5 - 0.07 + 1) % 1, A * 0.95, D);
    const front = (leg: typeof hL, sh: BB, el: BB, wr: BB): void => {
      b[sh].rot(leg.swing - 0.08 * leg.fold).pos(0, 0.045 * leg.load, 0);
      b[el].rot(0.95 * leg.fold - 0.1 * leg.reach);
      b[wr].rot(1.15 * leg.fold - 0.3 * leg.reach + 0.06 * leg.load);
    };
    const back = (leg: typeof hL, hip: BB, hock: BB, foot: BB): void => {
      // The haunch hitches up, the hock folds the paw forward and up, the sole turned back, dangling.
      b[hip].rot(leg.swing - 0.1 * leg.fold).pos(0, 0.06 * leg.fold, 0.02 * leg.fold);
      b[hock].rot(-0.55 * leg.fold + 0.05 * leg.load);
      b[foot].rot(1.0 * leg.fold - 0.15 * leg.reach);
    };
    front(fL, 'frontL', 'elbowL', 'wristL');
    front(fR, 'frontR', 'elbowR', 'wristR');
    back(hL, 'hindL', 'hockL', 'footL');
    back(hR, 'hindR', 'hockR', 'footR');
    // Two dips a cycle (one per diagonal pair), the bulk rolling onto the planted side.
    const th = u * TAU;
    b.root.pos(0, -0.008 + 0.022 * Math.cos(2 * th - 0.5), 0).rot(0.015 * Math.cos(2 * th), 0.035 * Math.sin(th - 0.3), 0.04 * Math.sin(th - 0.2));
    b.body.rot(0.03 + 0.02 * Math.cos(2 * th - 1), -0.05 * Math.sin(th - 0.6), -0.05 * Math.sin(th - 0.7));
    b.head.rot(0.2 + 0.05 * Math.cos(2 * th - 1.4), 0.16 * Math.sin(th - 1.3), 0.07 * Math.sin(th - 1.1));
    b.jaw.rot(0.04 * Math.max(0, Math.cos(2 * th - 1.6)));
  }, (t) => {
    const k = Math.cos(phase(t, dur) * TAU * 2 - 0.5);
    return [1 - 0.012 * k, 1 + 0.015 * k, 1];
  });

const B_STANCE: BP = {};

/**
 * The maul: a quick rear onto the haunches, both forepaws high, then down on the prey with the whole weight
 * (the blow at ~0.13 s), jaws snapping; a backhand rake with the left paw follows, and it settles back on all fours.
 */
const bearAttack = (): Clip<BB> =>
  clip<BB>('attack', 0.95, false, (b, t) => {
    const rear: BP = {
      root: [-0.42, 0, 0, 0, 0.3, 0.14], body: [-0.12, 0, 0], head: [-0.45, 0, 0], jaw: [0.45, 0, 0],
      frontL: [-1.5, 0, -0.25], elbowL: [-0.55, 0, 0], wristL: [0.7, 0, 0],
      frontR: [-1.6, 0, 0.25], elbowR: [-0.5, 0, 0], wristR: [0.75, 0, 0],
      hindL: [0.22, 0, 0], hockL: [0.08, 0, 0], footL: [0.12, 0, 0], hindR: [0.26, 0, 0], hockR: [0.06, 0, 0], footR: [0.1, 0, 0],
    };
    const slam: BP = {
      root: [0.16, 0, 0, 0, -0.02, 0.16], body: [0.12, 0, 0], head: [0.35, 0, 0], jaw: [0.55, 0, 0],
      frontL: [-0.55, 0, 0.05], elbowL: [0.25, 0, 0], wristL: [-0.25, 0, 0],
      frontR: [-0.6, 0, -0.05], elbowR: [0.25, 0, 0], wristR: [-0.25, 0, 0],
      hindL: [-0.12, 0, 0], hockL: [0.15, 0, 0], footL: [0.1, 0, 0], hindR: [-0.1, 0, 0], hockR: [0.12, 0, 0], footR: [0.08, 0, 0],
    };
    const settleP: BP = { ...slam, root: [0.1, 0, 0, 0, -0.01, 0.12], head: [0.25, 0, 0], jaw: [0.1, 0, 0], wristL: [0, 0, 0], wristR: [0, 0, 0] };
    // The backhand: the left paw comes up and rakes across to the right, the head ducking away from it.
    const rakeUp: BP = { ...settleP, root: [0.05, 0.15, 0.06, 0, -0.03, 0.08], frontL: [-1.2, 0.2, -0.55], elbowL: [-0.4, 0, 0], wristL: [0.4, 0, 0], head: [0.15, -0.3, 0], jaw: [0.3, 0, 0], body: [0.05, 0.12, 0.05] };
    const rake: BP = { ...settleP, root: [0.08, -0.18, -0.04, 0, -0.04, 0.1], frontL: [-0.65, -0.3, 0.55], elbowL: [0.1, 0, 0], wristL: [-0.3, 0, 0], head: [0.2, 0.25, 0], jaw: [0.45, 0, 0], body: [0.08, -0.12, -0.03] };
    timeline(b, t, [
      [0, B_STANCE],
      [0.085, rear, outQuad],
      [0.13, slam, inCubic],
      [0.24, settleP, outQuad],
      [0.38, rakeUp, inOut],
      [0.48, rake, inQuad],
      [0.62, { ...rake, frontL: [-0.3, -0.1, 0.25], elbowL: [0.05, 0, 0], wristL: [0, 0, 0], jaw: [0.1, 0, 0] }, outQuad],
      [0.95, B_STANCE, inOut],
    ], { head: 0.025, jaw: 0.015, wristL: 0.02, wristR: 0.02, elbowL: 0.01, elbowR: 0.01 });
  }, (t) => {
    const k = clampP(settle(t - 0.13, 3.5, 10));
    return [1 + 0.05 * k, 1 - 0.06 * k, 1];
  });

const bearHit = (): Clip<BB> =>
  clip<BB>('hit', 0.45, false, (b, t) => {
    const k = settle(t, 2.2, 7.5) * span(t, 0, 0.035, outQuad);
    b.root.pos(0, 0, -0.08 * k).rot(-0.06 * k, 0.05 * k, 0.04 * k);
    b.body.rot(-0.08 * k, 0.1 * k, 0.05 * k);
    b.head.rot(-0.4 * k, -0.35 * k, 0.15 * k);
    b.jaw.rot(0.4 * Math.abs(k));
    b.frontL.rot(0.12 * k);
    b.frontR.rot(0.1 * k);
    b.elbowL.rot(0.15 * Math.abs(k));
  }, (t) => {
    const k = clampP(settle(t, 3, 9));
    return [1 + 0.04 * k, 1 - 0.05 * k, 1];
  });

/** Down: the forelegs buckle, it slumps onto its chest and rolls over onto its left side, and lies there. */
const bearDown = (): Clip<BB> =>
  clip<BB>('down', 1.25, false, (b, t) => {
    const slump: BP = {
      root: [0.15, 0, 0.05, 0, -0.3, 0.06], body: [0.1, 0, 0.08], head: [0.45, 0.2, 0.1], jaw: [0.25, 0, 0],
      frontL: [-0.6, 0, -0.1], elbowL: [1.5, 0, 0], wristL: [0.4, 0, 0], frontR: [-0.5, 0, 0.1], elbowR: [1.35, 0, 0], wristR: [0.45, 0, 0],
      hindL: [-0.1, 0, -0.05], hockL: [0.2, 0, 0], footL: [0.1, 0, 0], hindR: [-0.1, 0, 0.05], hockR: [0.2, 0, 0], footR: [0.1, 0, 0],
    };
    const side: BP = {
      figure: [-1.45, 0, 0, 0, 0.2, 0], root: [0, 0, 0, 0, -0.02, 0], body: [0.05, 0, 0], head: [0.25, 0.5, 0.15], jaw: [0.2, 0, 0],
      frontL: [-0.25, 0, 0.7], elbowL: [0.45, 0, 0], wristL: [0.5, 0, 0], frontR: [-0.4, 0, -0.6], elbowR: [0.6, 0, 0], wristR: [0.6, 0, 0],
      hindL: [-0.15, 0, -0.12], hockL: [0.3, 0, 0], footL: [0.3, 0, 0], hindR: [-0.35, 0, 0.1], hockR: [0.4, 0, 0], footR: [0.35, 0, 0],
    };
    timeline(b, t, [
      [0, B_STANCE],
      [0.1, { ...B_STANCE, head: [-0.3, -0.2, 0], jaw: [0.35, 0, 0], body: [-0.05, 0, 0] }, outQuad],
      [0.42, slump, inQuad],
      [0.5, { ...slump, root: [0.17, 0, 0.06, 0, -0.32, 0.06] }, outQuad],
      [0.95, side, inCubic],
      [1.25, { ...side, figure: [-1.43, 0, 0, 0, 0.19, 0], head: [0.3, 0.55, 0.2] }, outBack],
    ], { head: 0.04, jaw: 0.03, wristL: 0.03, wristR: 0.03, footL: 0.03, footR: 0.03 });
  }, (t) => {
    const k = clampP(settle(t - 0.42, 3, 9)) * 0.6 + clampP(settle(t - 0.95, 3, 9));
    return [1 + 0.05 * k, 1 - 0.06 * k, 1];
  });

/** The transformation's roar: it rears up tall on its hind legs, throws its head back and bellows, and slams down. */
const bearRoar = (): Clip<BB> =>
  clip<BB>('roar', 1.7, false, (b, t) => {
    const crouch: BP = {
      root: [0.12, 0, 0, 0, -0.08, -0.04], body: [0.1, 0, 0], head: [0.4, 0, 0],
      frontL: [0.15, 0, 0], elbowL: [0.25, 0, 0], frontR: [0.15, 0, 0], elbowR: [0.25, 0, 0],
      hindL: [-0.2, 0, 0], hockL: [0.3, 0, 0], footL: [0.15, 0, 0], hindR: [-0.2, 0, 0], hockR: [0.3, 0, 0], footR: [0.15, 0, 0],
    };
    const tall: BP = {
      root: [-0.9, 0, 0, 0, 0.83, 0.35], body: [-0.15, 0, 0], head: [0.15, 0, 0], jaw: [0.2, 0, 0],
      frontL: [-0.6, 0, -0.4], elbowL: [-0.6, 0, 0], wristL: [0.6, 0, 0], frontR: [-0.6, 0, 0.4], elbowR: [-0.6, 0, 0], wristR: [0.6, 0, 0],
      hindL: [0.62, 0, -0.08], hockL: [0.12, 0, 0], footL: [0.16, 0, 0], hindR: [0.66, 0, 0.08], hockR: [0.1, 0, 0], footR: [0.14, 0, 0],
    };
    const bellow: BP = { ...tall, root: [-0.95, 0, 0, 0, 0.87, 0.37], head: [-0.45, 0, 0], jaw: [0.75, 0, 0], frontL: [-0.8, 0, -0.75], frontR: [-0.8, 0, 0.75], elbowL: [-0.45, 0, 0], elbowR: [-0.45, 0, 0], wristL: [0.3, 0, 0], wristR: [0.3, 0, 0] };
    const land: BP = {
      root: [0.14, 0, 0, 0, -0.1, 0.1], body: [0.1, 0, 0], head: [0.4, 0, 0], jaw: [0.6, 0, 0],
      frontL: [-0.45, 0, 0.05], elbowL: [0.35, 0, 0], wristL: [-0.2, 0, 0], frontR: [-0.45, 0, -0.05], elbowR: [0.35, 0, 0], wristR: [-0.2, 0, 0],
      hindL: [-0.15, 0, 0], hockL: [0.2, 0, 0], hindR: [-0.15, 0, 0], hockR: [0.2, 0, 0],
    };
    timeline(b, t, [
      [0, B_STANCE],
      [0.2, crouch, inOut],
      [0.48, tall, outCubic],
      [0.62, bellow, outQuad],
      [1.08, { ...bellow, root: [-0.93, 0, 0, 0, 0.85, 0.36], head: [-0.38, 0.1, 0], jaw: [0.65, 0, 0] }],
      [1.28, land, inQuad],
      [1.7, B_STANCE, inOut],
    ], { head: 0.04, jaw: 0.03, wristL: 0.03, wristR: 0.03, elbowL: 0.02, elbowR: 0.02 });
    // The bellow shakes it: a tremor through the head and shoulders while the jaws are wide.
    const roar = span(t, 0.6, 0.66) * (1 - span(t, 1.0, 1.1));
    b.head.rot(0, 0.06 * roar * Math.sin(t * TAU * 9), 0.05 * roar * Math.sin(t * TAU * 7));
    b.body.rot(0, 0, 0.02 * roar * Math.sin(t * TAU * 9 + 1));
  }, (t) => {
    const k = clampP(settle(t - 1.28, 3, 9));
    const up = span(t, 0.2, 0.48) * (1 - span(t, 1.08, 1.28));
    return [1 + 0.06 * k - 0.02 * up, 1 - 0.07 * k + 0.03 * up, 1];
  }, 'Rugido');

const bearClips = (): Clip[] => [bearIdle(), bearWalk(), bearAttack(), bearHit(), bearDown(), bearRoar()] as Clip[];

/* ---------- worker golems: squat stone, short legs, a tool in the right fist ---------- */

type WB = 'root' | 'body' | 'head' | 'legL' | 'legR' | 'armL' | 'armR' | 'tool' | 'lantern';
type WP = KeyPose<WB>;

const golemIdle = (): Clip<WB> =>
  clip<WB>('idle', 3.2, true, (b, t) => {
    const p = phase(t, 3.2) * TAU;
    // Stone doesn't breathe; the rune-light inside it settles in slow heaves, and the head turns now and then.
    const hv = Math.sin(p);
    b.body.rot(0.03 + 0.025 * hv, 0.04 * Math.sin(p + 0.7), 0.02 * Math.sin(p * 2));
    b.root.pos(0, -0.004 * (1 + hv), 0);
    const look = Math.max(0, Math.sin(p + 2)) ** 3;
    b.head.rot(-0.03 * hv, 0.45 * look * Math.sin(p * 2 + 0.3), 0);
    b.armL.rot(-0.04 * hv, 0, -0.03);
    b.armR.rot(-0.1 - 0.04 * Math.sin(p + 0.5), 0, 0.03);
    b.tool.rot(0.05 * Math.sin(p + 1));
    b.lantern.rot(0.06 * Math.sin(p * 2 + 0.4), 0, 0.04 * Math.sin(p));
    b.legL.rot(0, 0, -0.02 * Math.sin(p));
    b.legR.rot(0, 0, -0.02 * Math.sin(p));
  });

/** A heavy, rocking waddle: short stiff legs, the whole block rolls over the planted foot. */
const golemWalk = (dur = 0.7): Clip<WB> =>
  clip<WB>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), cs = Math.cos(th);
    b.legL.rot(-0.55 * sn, 0, 0);
    b.legR.rot(0.55 * sn, 0, 0);
    // Stiff legs: the hips drop as the stride opens (a thump on each contact) and rise as a leg passes under;
    // the block rolls onto the standing foot.
    b.root.pos(0, 0.012 * cs * cs - 0.03 * sn * sn, 0).rot(0, 0.06 * sn, -0.09 * sn);
    b.body.rot(0.1 + 0.04 * Math.cos(2 * th), -0.12 * sn, 0.05 * sn);
    b.head.rot(-0.05 - 0.04 * Math.cos(2 * th - 0.7), 0.1 * sn, 0.06 * sn);
    b.armL.rot(0.4 * Math.sin(th - 0.5), 0, -0.06);
    b.armR.rot(-0.3 * Math.sin(th - 0.5) - 0.12, 0, 0.06);
    b.tool.rot(0.15 * Math.sin(th - 1.2));
    b.lantern.rot(0.25 * Math.sin(th - 1.6) + 0.1 * Math.sin(2 * th - 2.2), 0, 0.12 * Math.sin(th - 1.4));
  }, (t) => {
    const k = Math.cos(phase(t, dur) * TAU * 2);
    return [1 - 0.02 * k, 1 + 0.025 * k, 1 - 0.02 * k];
  });

/** Golems at the work site: the tool comes down at 3/4 of the loop, when the dust flies. */
const WORK_DUR = 0.5;
const W_HIT = 0.375;
const golemWork = (): Clip<WB> =>
  clip<WB>('work', WORK_DUR, true, (b, t) => {
    const raised: WP = { armR: [-2.6, 0, 0.1], tool: [-0.5, 0, 0], armL: [-0.3, 0, -0.2], body: [-0.2, 0.18, 0], head: [-0.15, 0, 0], root: [0, 0, 0, 0, 0.012, 0], legL: [-0.15, 0, 0], legR: [0.12, 0, 0], lantern: [-0.3, 0, 0] };
    const strike: WP = { armR: [-0.55, 0, -0.05], tool: [0.55, 0, 0], armL: [-0.55, 0, -0.1], body: [0.42, -0.08, 0], head: [0.25, 0, 0], root: [0, 0, 0, 0, -0.03, 0], legL: [-0.2, 0, 0], legR: [0.15, 0, 0], lantern: [0.5, 0, 0] };
    const rebound: WP = { ...strike, armR: [-0.85, 0, -0.03], tool: [0.3, 0, 0], body: [0.34, -0.06, 0], root: [0, 0, 0, 0, -0.022, 0], lantern: [-0.15, 0, 0] };
    timeline(b, t, [
      [0, rebound],
      [0.06, rebound],
      [0.25, raised, inOut],
      [W_HIT, strike, inCubic],
      [0.43, rebound, outQuad],
      [WORK_DUR, rebound],
    ], { head: 0.02, armL: 0.015 });
  }, (t) => {
    const k = Math.max(0, settle(t - W_HIT, 4, 14));
    return [1 + 0.06 * k, 1 - 0.07 * k, 1 + 0.06 * k];
  });

/** Carrying a load high in both arms: a slower, braced walk. */
const golemCarry = (dur = 0.84): Clip<WB> =>
  clip<WB>('carry', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), cs = Math.cos(th);
    b.legL.rot(-0.42 * sn, 0, 0);
    b.legR.rot(0.42 * sn, 0, 0);
    b.root.pos(0, 0.008 * cs * cs - 0.025 * sn * sn, 0).rot(0, 0.03 * sn, -0.07 * sn);
    b.body.rot(-0.12 + 0.03 * Math.cos(2 * th), -0.05 * sn, 0.04 * sn);
    b.head.rot(0.1, 0.05 * sn, 0);
    // Both arms up and forward under the load, jostled by each step.
    const jolt = 0.06 * Math.cos(2 * th - 0.5);
    b.armL.rot(-1.9 + jolt, 0, 0.32);
    b.armR.rot(-1.9 + jolt, 0, -0.32);
    b.tool.rot(0.8);
    b.lantern.rot(-0.6 + 0.15 * Math.sin(th - 1.2));
  }, (t) => {
    const k = Math.cos(phase(t, dur) * TAU * 2);
    return [1 - 0.025 * k, 1 + 0.03 * k, 1 - 0.025 * k];
  }, 'Cargar');

const golemClips = (): Clip[] => [golemIdle(), golemWalk(), golemWork(), golemCarry()] as Clip[];

/* ---------- clockwork automaton and colossus: iron weight, purposeful snaps and settles ---------- */

type CB = 'root' | 'figure' | 'body' | 'head' | 'armL' | 'foreL' | 'armR' | 'foreR' | 'weapon' | 'legL' | 'shinL' | 'legR' | 'shinR' | 'gearHip' | 'gearBack';
type CP = KeyPose<CB>;

/** The gears' symmetry (ten teeth, an eight-sided hub): a turn of half a circle looks the same. */
const GEAR_SYM = Math.PI;
/** An escapement: `n` clicks over [0, 1), each a snap that overshoots and settles; turns `n` teeth. */
const ratchet = (u: number, n: number, tooth: number): number => {
  const x = u * n;
  const i = Math.floor(x);
  const f = x - i;
  return (i + outBack(Math.min(1, f / 0.35))) * tooth;
};

/** One leg's stomp over its cycle `u`: the stance drives it back, the swing hauls it forward and plants it hard. */
const stomp = (u: number, A: number, lift: number, duty = 0.58): { thigh: number; knee: number } => {
  if (u < duty) {
    const k = u / duty;
    return { thigh: -A + 2 * A * k, knee: 0.06 + 0.1 * Math.max(0, 1 - k * 4) };
  }
  const k = (u - duty) / (1 - duty);
  // Knee up fast, thigh forward, then the foot slams down with the knee locking.
  const thigh = A - 2 * A * inOut(Math.min(1, k * 1.15));
  const knee = lift * Math.sin(Math.PI * Math.min(1, k * 1.1)) ** 0.8 + 0.06;
  return { thigh, knee };
};

interface BotStyle {
  big: boolean;
  /** Walk cycle (two steps), seconds. */
  walk: number;
}

const botIdle = (s: BotStyle): Clip<CB> => {
  const D = s.big ? 4.8 : 4;
  return clip<CB>('idle', D, true, (b, t) => {
    const u = phase(t, D);
    const p = u * TAU;
    // The boiler breathes in two strokes a loop: a hiss up, held, and a sigh down.
    const stroke = (x: number): number => { const f = (x * 2) % 1; return f < 0.35 ? span(f, 0, 0.35, inOut) : f < 0.5 ? 1 : 1 - span(f, 0.5, 0.95, inOut); };
    const br = stroke(u);
    b.body.rot(-0.035 * br, 0, 0).scale(1 + 0.012 * br);
    b.figure.pos(0, 0.012 * br - 0.006, 0);
    b.shinL.rot(0.04 * (1 - br));
    b.shinR.rot(0.04 * (1 - br));
    b.legL.rot(-0.02 * (1 - br));
    b.legR.rot(-0.02 * (1 - br));
    b.armL.rot(0.04 * br, 0, -0.05 * br);
    b.armR.rot(0.04 * br, 0, 0.05 * br);
    b.foreL.rot(-0.06 * br);
    b.foreR.rot(-0.05 * br);
    // The head scans: holds, then snaps to a new bearing and settles (a mechanical look-around).
    const look = keysOf(u, [[0, 0], [0.18, 0], [0.24, 0.55, outBack], [0.45, 0.55], [0.5, -0.35, outBack], [0.72, -0.35], [0.78, 0, outBack], [1, 0]]);
    b.head.rot(0.03 * br, look, 0);
    b.weapon.rot(0.03 * Math.sin(p));
    b.gearHip.rot(0, 0, ratchet(u, 5, GEAR_SYM / 5));
    b.gearBack.rot(0, 0, -ratchet((u + 0.1) % 1, 5, GEAR_SYM / 5));
  });
};

/** Local keyframes over a loop phase (helper for snappy, held motions). */
const keysOf = (u: number, ks: readonly (readonly [number, number, ((k: number) => number)?])[]): number => {
  for (let i = 1; i < ks.length; i++) {
    const z = ks[i]!;
    if (u <= z[0]) {
      const a = ks[i - 1]!;
      const k = z[0] > a[0] ? (u - a[0]) / (z[0] - a[0]) : 1;
      return a[1] + (z[1] - a[1]) * (z[2] ?? inOut)(k);
    }
  }
  return ks[ks.length - 1]![1];
};

/** A heavy mechanical stomp: each foot is hauled up and slammed down; the frame jolts and settles on contact. */
const botWalk = (s: BotStyle): Clip<CB> => {
  const D = s.walk;
  const A = s.big ? 0.36 : 0.42;
  const L = s.big ? 0.92 : 1.0;
  return clip<CB>('walk', D, true, (b, t) => {
    const u = phase(t, D);
    const l = stomp(u, A, s.big ? 0.85 : 0.95);
    const r = stomp((u + 0.5) % 1, A, s.big ? 0.85 : 0.95);
    b.legL.rot(l.thigh - 0.05 * l.knee, 0, -0.03);
    b.shinL.rot(l.knee);
    b.legR.rot(r.thigh - 0.05 * r.knee, 0, 0.03);
    b.shinR.rot(r.knee);
    // Contact at u = 0 (left) and 0.5 (right): the hips sit lowest on the planted, driving leg, with a jolt.
    const stance = u < 0.5 ? l.thigh : r.thigh;
    const since = (u % 0.5) * D;
    const jolt = settle(since, s.big ? 3.2 : 3.8, s.big ? 9 : 11);
    const drop = L * (1 - Math.cos(stance)) * 0.3 + (s.big ? 0.03 : 0.025) * clampP(jolt);
    b.figure.pos(0, -drop + 0.01, 0).rot(0, 0, 0);
    // Weight rolls onto the standing leg; the torso swivels against the stride, with a beat of lag.
    const side = Math.sin(u * TAU);
    b.root.rot(0, 0, (s.big ? 0.05 : 0.04) * side);
    b.body.rot(0.08 + 0.05 * clampP(jolt), -0.12 * Math.sin(u * TAU - 0.4), -0.03 * side);
    b.head.rot(-0.05 - 0.05 * clampP(jolt), 0.08 * Math.sin(u * TAU - 0.4), 0.02 * side);
    // Stiff arms swing against the legs; the forearms lag and bounce on each contact.
    const sw = Math.sin(u * TAU - 0.5);
    b.armL.rot(0.3 * sw, 0, -0.04);
    b.armR.rot(-0.22 * sw, 0, 0.04);
    b.foreL.rot(-0.15 - 0.15 * clampP(-sw) - 0.06 * clampP(jolt));
    // The hammer is carried clear of the ground: elbow cocked, the head swinging a beat behind.
    b.foreR.rot((s.big ? -0.6 : -0.45) - 0.12 * clampP(sw) - 0.06 * clampP(jolt));
    b.weapon.rot((s.big ? 0.4 : 0.3) + 0.14 * Math.sin(u * TAU - 1.3), 0, 0.04 * Math.sin(u * TAU * 2 - 1));
    b.gearHip.rot(0, 0, u * GEAR_SYM * 2);
    b.gearBack.rot(0, 0, -u * GEAR_SYM * 2);
  }, (t) => {
    const u = phase(t, D);
    const j = clampP(settle((u % 0.5) * D, 3.8, 11));
    return [1 + 0.03 * j, 1 - 0.035 * j, 1 + 0.03 * j];
  });
};

/**
 * The sweep: a flash of windup (hammer cocked back to the right, torso wound) and the hammer swung flat right
 * round it, a full turn on its heels; the blow passes the front at ~0.12 s. It plants, stomps back square and settles.
 */
const botAttack = (s: BotStyle): Clip<CB> => {
  const D = s.big ? 1.25 : 1.05;
  const end = TAU;
  const windup: CP = {
    figure: [0, 0.55, 0, 0, -0.06, 0], body: [-0.12, 0.6, 0.05], head: [0, -0.45, 0],
    armR: [-0.5, 0, 1.0], foreR: [-0.35, 0, 0], weapon: [0.2, 0, 0.3], armL: [-0.7, 0, -0.5], foreL: [-0.8, 0, 0],
    legL: [-0.32, 0, -0.1], shinL: [0.35, 0, 0], legR: [0.25, 0, 0.12], shinR: [0.22, 0, 0], gearHip: [0, 0, 0],
  };
  const hit: CP = {
    figure: [0, -1.55, 0, 0, -0.1, 0], body: [0.1, -0.55, -0.08], head: [0.05, 0.3, 0],
    armR: [-0.3, 0, 1.4], foreR: [-0.05, 0, 0], weapon: [0, 0, 0.25], armL: [0.1, 0, -0.85], foreL: [-0.5, 0, 0],
    legL: [-0.38, 0, -0.14], shinL: [0.45, 0, 0], legR: [0.3, 0, 0.14], shinR: [0.3, 0, 0], gearHip: [0, 0, GEAR_SYM * 0.6],
  };
  const through: CP = {
    figure: [0, -4.1, 0, 0, -0.12, 0], body: [0.16, -0.75, -0.06], head: [0.08, 0.35, 0],
    armR: [-0.4, 0, 1.25], foreR: [-0.15, 0, 0], weapon: [0, 0, 0.1], armL: [0.2, 0, -0.75], foreL: [-0.6, 0, 0],
    legL: [-0.35, 0, -0.14], shinL: [0.42, 0, 0], legR: [0.3, 0, 0.14], shinR: [0.28, 0, 0], gearHip: [0, 0, GEAR_SYM * 1.4],
  };
  // Stomping round to face front again: two heavy steps.
  const stepL: CP = { figure: [0, -5.1, 0, 0, -0.04, 0], body: [0.08, -0.3, 0.03], head: [0, 0.15, 0], armR: [-0.3, 0, 0.5], foreR: [-0.2, 0, 0], armL: [0.05, 0, -0.3], foreL: [-0.35, 0, 0], legL: [-0.55, 0, -0.05], shinL: [0.9, 0, 0], legR: [0.15, 0, 0.05], shinR: [0.12, 0, 0], gearHip: [0, 0, GEAR_SYM * 1.7] };
  const plantL: CP = { figure: [0, -5.6, 0, 0, -0.06, 0], body: [0.1, -0.15, 0], armR: [-0.2, 0, 0.2], foreR: [-0.15, 0, 0], armL: [0, 0, -0.1], foreL: [-0.25, 0, 0], legL: [-0.15, 0, 0], shinL: [0.15, 0, 0], legR: [0.1, 0, 0], shinR: [0.15, 0, 0], gearHip: [0, 0, GEAR_SYM * 1.85] };
  const stepR: CP = { figure: [0, -6.0, 0, 0, -0.03, 0], body: [0.05, -0.05, -0.02], armR: [-0.1, 0, 0.08], armL: [0.05, 0, -0.05], foreL: [-0.1, 0, 0], legR: [-0.5, 0, 0.05], shinR: [0.85, 0, 0], legL: [0.12, 0, 0], shinL: [0.1, 0, 0], gearHip: [0, 0, GEAR_SYM * 1.95] };
  const square: CP = { figure: [0, -end, 0, 0, -0.03, 0], body: [0.04, 0, 0], legL: [0, 0, 0], legR: [0, 0, 0], shinL: [0.08, 0, 0], shinR: [0.08, 0, 0], gearHip: [0, 0, GEAR_SYM * 2] };
  const rest: CP = { figure: [0, -end, 0], gearHip: [0, 0, GEAR_SYM * 2] };
  const k = s.big ? 1.15 : 1;
  return clip<CB>('attack', D, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.065, windup, outQuad],
      [0.12, hit, inQuad],
      [0.24 * k, through, outCubic],
      [0.42 * k, stepL, inOut],
      [0.52 * k, plantL, inQuad],
      [0.64 * k, stepR, inOut],
      [0.74 * k, square, inQuad],
      [D, rest, inOut],
    ], { weapon: 0.025, foreR: 0.012, head: 0.03, foreL: 0.015 });
  }, (t) => {
    const j = clampP(settle(t - 0.12, 4, 10)) + 0.6 * clampP(settle(t - 0.52 * k, 4, 12)) + 0.6 * clampP(settle(t - 0.74 * k, 4, 12));
    return [1 + 0.03 * j, 1 - 0.04 * j, 1 + 0.03 * j];
  });
};

/** Dropped from the sky: it falls, lands in a deep crouch with a jolt, steam-hisses up and squares its shoulders. */
const botSpawn = (s: BotStyle): Clip<CB> => {
  const D = s.big ? 0.75 : 0.6;
  const LAND = s.big ? 0.15 : 0.13;
  const H = s.big ? 3.2 : 2.6;
  const crouch: CP = {
    figure: [0, 0, 0, 0, -0.2, 0], body: [0.5, 0, 0], head: [-0.3, 0, 0],
    armL: [-0.55, 0, -0.55], foreL: [-0.5, 0, 0], armR: [-0.45, 0, 0.6], foreR: [-0.4, 0, 0], weapon: [0.3, 0, 0.2],
    legL: [-0.9, 0, -0.15], shinL: [1.1, 0, 0], legR: [-0.85, 0, 0.15], shinR: [1.05, 0, 0],
  };
  const fall: CP = {
    figure: [0, 0, 0, 0, H, 0], body: [-0.1, 0, 0], head: [0.1, 0, 0],
    armL: [-0.3, 0, -0.75], foreL: [-0.3, 0, 0], armR: [-0.3, 0, 0.8], foreR: [-0.2, 0, 0], weapon: [-0.2, 0, 0.3],
    legL: [-0.25, 0, -0.05], shinL: [0.35, 0, 0], legR: [-0.15, 0, 0.05], shinR: [0.3, 0, 0],
  };
  return clip<CB>('spawn', D, false, (b, t) => {
    timeline(b, t, [
      [0, fall],
      [LAND, { ...crouch, figure: [0, 0, 0, 0, -0.1, 0] }, inQuad],
      [LAND + 0.07, crouch, outQuad],
      [LAND + 0.16, crouch],
      [D - 0.12, { body: [-0.06, 0, 0], head: [-0.06, 0, 0], figure: [0, 0, 0, 0, 0.02, 0], armL: [0.05, 0, -0.15], armR: [0.05, 0, 0.15] }, outBack],
      [D, {}, inOut],
    ], { head: 0.025, foreL: 0.02, foreR: 0.02, weapon: 0.03 });
    b.gearHip.rot(0, 0, ratchet(span(t, LAND, D, linear), 5, GEAR_SYM / 5));
    b.gearBack.rot(0, 0, -ratchet(span(t, LAND, D, linear), 5, GEAR_SYM / 5));
  }, (t) => {
    const j = clampP(settle(t - LAND, 3, 9));
    const fallS = t < LAND ? 0.06 * span(t, 0, LAND, inQuad) : 0;
    return [1 + 0.1 * j - fallS * 0.5, 1 - 0.13 * j + fallS, 1 + 0.1 * j - fallS * 0.5];
  }, 'Aterrizaje');
};

/** A blow: the frame rocks back, the head snaps, it re-plants. */
const botHit = (s: BotStyle): Clip<CB> =>
  clip<CB>('hit', s.big ? 0.45 : 0.38, false, (b, t) => {
    const k = settle(t, s.big ? 2 : 2.5, 8) * span(t, 0, 0.03, outQuad);
    b.body.rot(-0.22 * k, 0.1 * k, 0.06 * k);
    b.head.rot(-0.35 * k, -0.2 * k, 0.1 * k);
    b.armL.rot(0.25 * k, 0, -0.2 * k);
    b.armR.rot(0.2 * k, 0, 0.25 * k);
    b.foreL.rot(-0.3 * Math.abs(k));
    b.figure.pos(0, 0, -0.08 * k);
    b.legL.rot(0.1 * k);
    b.shinR.rot(0.15 * Math.abs(k));
  });

/**
 * Broken: the core gutters, it jerks (spasms in its joints), the knees give, it slumps to its knees and pitches
 * forward onto its face. Held on the ground.
 */
const botDie = (s: BotStyle): Clip<CB> => {
  const D = s.big ? 1.7 : 1.5;
  const k = s.big ? 1.12 : 1;
  const lie = s.big ? 0.3 : 0.28;
  const jerk: CP = { body: [-0.18, 0.25, 0.08], head: [-0.4, -0.35, 0.2], armR: [-0.4, 0, 0.7], foreR: [-0.6, 0, 0], armL: [-0.2, 0, -0.5], foreL: [-0.7, 0, 0], figure: [0, 0, 0, 0, 0.02, 0] };
  const jerk2: CP = { body: [0.1, -0.2, -0.1], head: [0.3, 0.4, -0.25], armR: [0.1, 0, 0.3], foreR: [-0.2, 0, 0], armL: [-0.5, 0, -0.2], foreL: [-1.0, 0, 0], legL: [0.05, 0, 0], shinL: [0.2, 0, 0], figure: [0, 0, 0, 0, -0.05, 0] };
  const knees: CP = {
    figure: [0, 0.15, 0, 0, s.big ? -0.46 : -0.5, 0.05], body: [0.35, 0.15, 0.12], head: [0.6, 0.2, 0.3],
    armR: [0.05, 0, 0.15], foreR: [-0.1, 0, 0], weapon: [0.6, 0, 0], armL: [0.1, 0, -0.1], foreL: [-0.2, 0, 0],
    legL: [-0.25, 0, -0.12], shinL: [1.6, 0, 0], legR: [-0.15, 0, 0.12], shinR: [1.55, 0, 0],
  };
  const face: CP = {
    figure: [1.38, 0.2, 0.08, 0, lie, -0.5], body: [0.25, 0.15, 0.15], head: [0.35, 0.5, 0.25],
    armR: [-2.6, 0, 0.6], foreR: [-0.2, 0, 0], weapon: [0.4, 0, 0], armL: [-2.3, 0, -0.6], foreL: [-0.4, 0, 0],
    legL: [-0.4, 0, -0.12], shinL: [0.6, 0, 0], legR: [-0.3, 0, 0.12], shinR: [0.5, 0, 0],
  };
  return clip<CB>('die', D, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.08, jerk, outExpo],
      [0.2, jerk2, outQuad],
      [0.3, { ...jerk, head: [0.5, -0.1, 0.1], figure: [0, 0, 0, 0, -0.08, 0] }, outBack],
      [0.62 * k, knees, inQuad],
      [0.72 * k, { ...knees, figure: [0.1, 0.15, 0, 0, s.big ? -0.47 : -0.51, 0.05] }, outQuad],
      [1.1 * k, face, inCubic],
      [D, { ...face, figure: [1.42, 0.2, 0.08, 0, lie - 0.02, -0.5] }, outBack],
    ], { head: 0.04, foreL: 0.03, foreR: 0.03, weapon: 0.05 });
    // The gears stutter and stop.
    b.gearHip.rot(0, 0, ratchet(span(t, 0, 0.5, outCubic), 3, GEAR_SYM / 5));
  }, (t) => {
    const j = clampP(settle(t - 0.62 * k, 3, 9)) * 0.5 + clampP(settle(t - 1.1 * k, 3, 9));
    return [1 + 0.07 * j, 1 - 0.08 * j, 1 + 0.07 * j];
  });
};

const botClips = (s: BotStyle) => (): Clip[] => [botIdle(s), botWalk(s), botAttack(s), botHit(s), botSpawn(s), botDie(s)] as Clip[];

/* ---------- spider-bot: six legs on a ticking shell, quick and twitchy ---------- */

const SPIDER_LEGS = (['L', 'R'] as const).flatMap((side) => [0, 1, 2].map((j) => ({
  side, j, hip: `hip${side}${j}`, lift: `lift${side}${j}`,
  // Tripod sets: the front and back legs of one side step with the middle leg of the other.
  set: (j === 1 ? 1 : 0) ^ (side === 'R' ? 1 : 0),
  // The hip's yaw that swings this leg toward +Z.
  fwd: side === 'R' ? -1 : 1,
})));

/** A fast scuttle: two tripods trading off, the shell bobbing twice a cycle and the charge jiggling behind. */
const spiderWalk = (dur = 0.36): Clip => {
  const F = 0.3;
  return clip('walk', dur, true, (b, t) => {
    const u = phase(t, dur);
    for (const l of SPIDER_LEGS) {
      // Each tripod swings for half the cycle (its foot up and forward) while the other drives back.
      const v = (u + (l.set ? 0.5 : 0)) % 1;
      const swing = v < 0.5 ? v / 0.5 : 0;
      const yaw = v < 0.5 ? -F + 2 * F * inOut(swing) : F - 2 * F * ((v - 0.5) / 0.5);
      b[l.hip]!.rot(0, l.fwd * yaw, 0);
      b[l.lift]!.rot(0, 0, 0.5 * Math.sin(Math.PI * swing) ** 0.8);
    }
    const th = u * TAU;
    b.body!.pos(0, 0.012 * Math.cos(2 * th), 0).rot(0.04, 0.05 * Math.sin(th), 0.06 * Math.sin(th + 0.3));
    b.charge!.rot(0.12 * Math.sin(2 * th - 1.2), 0, 0.1 * Math.sin(th - 1));
  });
};

/** Twitchy: it holds, then snaps to a new bearing; feet tap and re-plant, one at a time. */
const spiderIdle = (): Clip => {
  const D = 3;
  return clip('idle', D, true, (b, t) => {
    const u = phase(t, D);
    const look = keys(u, [[0, 0], [0.2, 0], [0.24, 0.3, outBack], [0.55, 0.3], [0.58, -0.2, outBack], [0.85, -0.2], [0.9, 0, outBack]]);
    const tremble = 0.01 * Math.sin(t * TAU * 11);
    b.body!.rot(0.03 + tremble, look, 0.02 * Math.sin(u * TAU)).pos(0, 0.006 * Math.sin(u * TAU * 2), 0);
    // A foot tap every so often (each a quick lift, shuffle and plant).
    const taps: [string, number][] = [['L2', 0.1], ['R0', 0.3], ['R2', 0.5], ['L1', 0.66], ['L0', 0.8], ['R1', 0.93]];
    for (const [leg, at] of taps) {
      const k = span(u, at, at + 0.03, outQuad) * (1 - span(u, at + 0.05, at + 0.09, inQuad));
      const l = SPIDER_LEGS.find((x) => `${x.side}${x.j}` === leg)!;
      b[l.lift]!.rot(0, 0, 0.45 * k);
      b[l.hip]!.rot(0, l.fwd * 0.12 * k, 0);
    }
    // The charge pulses, ticking.
    b.charge!.scale(1 + 0.04 * Math.max(0, Math.sin(u * TAU * 6)) ** 6);
  });
};

/** Estallido: it crouches, trembling, springs up at the foe and swells to burst (the view removes it). */
const spiderAttack = (): Clip =>
  clip('attack', 0.42, false, (b, t) => {
    const crouch = span(t, 0, 0.16, outQuad) * (1 - span(t, 0.18, 0.24, inQuad));
    const air = span(t, 0.18, 0.3, outCubic);
    const swell = span(t, 0.26, 0.42, inQuad);
    const tremble = crouch * 0.02 * Math.sin(t * TAU * 18);
    b.body!.pos(0, -0.1 * crouch + 0.38 * air - 0.05 * span(t, 0.34, 0.42, inQuad), 0.18 * air).rot(0.25 * crouch - 0.35 * air + tremble, 0, tremble).scale(1 + 0.3 * swell);
    for (const l of SPIDER_LEGS) {
      const front = l.j === 2 ? 1 : l.j === 0 ? -1 : 0;
      // Crouched, the feet stay planted (the legs fold up); in the air they fling out and hang.
      b[l.lift]!.rot(0, 0, 0.42 * crouch - 0.55 * air + 0.25 * swell);
      b[l.hip]!.rot(0, l.fwd * (0.1 * front * crouch + 0.2 * front * air), 0);
    }
    b.charge!.rot(0.25 * crouch - 0.3 * air, 0, 0).scale(1 + 0.45 * swell);
  }, (t) => {
    const crouch = span(t, 0, 0.16, outQuad) * (1 - span(t, 0.18, 0.22, inQuad));
    const air = span(t, 0.18, 0.26, outQuad) * (1 - span(t, 0.26, 0.34));
    return [1 + 0.1 * crouch - 0.06 * air, 1 - 0.12 * crouch + 0.12 * air, 1 + 0.1 * crouch - 0.06 * air];
  }, 'Estallido');

const spiderClips = (): Clip[] => [spiderIdle(), spiderWalk(), spiderAttack()];

/* ---------- the caravan: a wagon rolling on four wheels, a quarry golem pushing it ---------- */

type KB = 'root' | 'wagon' | 'wheelBL' | 'wheelBR' | 'wheelFL' | 'wheelFR' | 'lanternL' | 'lanternR' | 'golem' | 'legL' | 'legR';
const WHEELS: KB[] = ['wheelBL', 'wheelBR', 'wheelFL', 'wheelFR'];
/** The golem's legs at rest are mid-stride (the old first frame): clips set absolute angles minus these. */
const CART_LEG_REST = { legL: 0.45, legR: -0.35 } as const;
/**
 * A wheel's turn per walk loop: half a circle, which every part of it repeats (six spokes across, an eight-sided
 * hub, a fourteen-sided rim), so the loop is seamless. With a wheel radius of 0.2 that is 0.63 model units rolled.
 */
const CART_TURN = Math.PI;

/** Rolling: the wheels turn, the wagon rocks over the ruts, the lanterns swing behind, the golem strides and shoves. */
const cartWalk = (dur = 0.9): Clip<KB> =>
  clip<KB>('walk', dur, true, (b, t) => {
    const u = phase(t, dur);
    const th = u * TAU;
    for (const w of WHEELS) b[w].rot(CART_TURN * u);
    // The wagon: a slow roll and a pitch over each rut (two a loop), a beat behind the golem's shove.
    b.wagon.rot(0.012 * Math.sin(2 * th - 0.8), 0, 0.018 * Math.sin(th + 0.4)).pos(0, 0.006 * Math.max(0, Math.sin(2 * th - 0.8)), 0);
    b.lanternL.rot(0.1 * Math.sin(2 * th - 2.2) + 0.08, 0, -0.06 * Math.sin(th - 0.8));
    b.lanternR.rot(0.1 * Math.sin(2 * th - 2.4) + 0.08, 0, -0.06 * Math.sin(th - 1.0));
    // The golem: one stride a loop (two steps), dipping and leaning in on each push-off.
    const sn = Math.sin(th);
    const lL = -0.5 * sn + 0.05, lR = 0.5 * sn + 0.05;
    b.legL.rot(lL - CART_LEG_REST.legL);
    b.legR.rot(lR - CART_LEG_REST.legR);
    b.golem.pos(0, 0.02 * Math.cos(2 * th) - 0.03, 0).rot(0.035 * Math.cos(2 * th - 0.6), 0.04 * sn, -0.04 * sn);
  }, (t) => {
    const k = Math.cos(phase(t, dur) * TAU * 2);
    return [1, 1 + 0.01 * k, 1];
  });

/** Stopped: the golem braces against the handle, settling its weight; the lanterns sway on their hooks. */
const cartIdle = (): Clip<KB> =>
  clip<KB>('idle', 4, true, (b, t) => {
    const u = phase(t, 4);
    const th = u * TAU;
    b.legL.rot(0.2 - CART_LEG_REST.legL + 0.03 * Math.sin(th));
    b.legR.rot(-0.22 - CART_LEG_REST.legR - 0.03 * Math.sin(th));
    b.golem.pos(0, -0.012 + 0.006 * Math.sin(2 * th), 0).rot(0.03 + 0.015 * Math.sin(2 * th), 0.03 * Math.sin(th + 1), 0);
    b.lanternL.rot(0.06 * Math.sin(2 * th), 0, 0.05 * Math.sin(th + 0.6));
    b.lanternR.rot(0.06 * Math.sin(2 * th + 0.9), 0, 0.05 * Math.sin(th + 1.4));
    b.wagon.rot(0.004 * Math.sin(2 * th + 0.3), 0, 0);
  });

/** A blow: the wagon lurches and rocks on its springs, the lanterns swing wild, the golem staggers and re-plants. */
const cartHit = (): Clip<KB> =>
  clip<KB>('hit', 0.7, false, (b, t) => {
    const k = settle(t, 2.2, 6) * span(t, 0, 0.04, outQuad);
    const kl = settle(t - 0.05, 1.6, 4) * span(t, 0.03, 0.09, outQuad);
    b.wagon.rot(-0.04 * k, 0.03 * k, 0.06 * k).pos(0.03 * k, 0.02 * Math.abs(k), 0);
    b.lanternL.rot(-0.35 * kl, 0, 0.3 * kl);
    b.lanternR.rot(-0.32 * kl, 0, 0.34 * kl);
    for (const w of WHEELS) b[w].rot(-0.08 * k);
    b.golem.rot(-0.1 * k, 0.06 * k, 0.05 * k).pos(0, 0.01 * Math.abs(k), -0.03 * k);
    b.legL.rot(-0.15 * k);
    b.legR.rot(0.12 * k);
  }, (t) => {
    const k = clampP(settle(t, 3, 9));
    return [1 + 0.03 * k, 1 - 0.03 * k, 1 + 0.03 * k];
  });

const cartClips = (): Clip[] => [cartIdle(), cartWalk(), cartHit()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = {
  bear: bearClips,
  golem: golemClips,
  'bot:automaton': botClips({ big: false, walk: 1.0 }),
  'bot:colossus': botClips({ big: true, walk: 1.2 }),
  'bot:spider': spiderClips,
  'bot:cart': cartClips,
  'bot:barrelCart': cartClips,
};
